import type {
  DatatableDefinition,
  DatatableLocalization,
  DatatableActionDefinition,
} from '../../../types/datatable-types';
import { initFetchModal } from '../modal-fetch';
import { initConfirmButtons } from '../modal-confirm';

/**
 * Executes a non-modal table action with optional selected row IDs
 * @param action - Action definition
 * @param selectedIds - Array of selected row IDs (for mass actions)
 */
async function executeAction(
  action: DatatableActionDefinition,
  selectedIds: string[]
): Promise<void> {
  const url = new URL(action.Url, window.location.origin);

  if (action.HttpMethod.toLowerCase() === 'get') {
    // Append IDs to query string for mass actions
    if (action.MassActionSetting.IsMassAction && selectedIds.length > 0) {
      selectedIds.forEach((id) => url.searchParams.append('ids', id));
    } else {
      // append query string as its currently set on page
      url.search = new URLSearchParams(window.location.search).toString();
    }

    // Handle Link action type with OpenInNewTab
    if (action.ActionType === 2 && action.OpenInNewTab) {
      window.open(url.toString(), '_blank', 'noopener');
    } else {
      window.location.href = url.toString();
    }
  } else {
    // POST request
    const body = action.MassActionSetting.IsMassAction
      ? JSON.stringify({ ids: selectedIds })
      : null;

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: action.MassActionSetting.IsMassAction ? { 'Content-Type': 'application/json' } : {},
      body: body,
    });

    if (response.ok) {
      window.location.reload();
    } else {
      // eslint-disable-next-line no-console
      console.error('Action failed:', action.Name);
    }
  }
}

/**
 * Initialize table actions for a datatable instance
 * @param container - .datatable container element
 * @param definition - Datatable definition with table actions
 * @param localization - Localization strings
 * @param getSelectedIds - Function to retrieve selected row IDs
 */
export function initTableActions(
  container: HTMLElement,
  definition: DatatableDefinition,
  localization: DatatableLocalization,
  getSelectedIds: () => string[]
): void {
  if (definition.TableActions.length === 0) return;

  // Find card header to insert actions
  const cardHeader = container.querySelector<HTMLElement>('.card-header');
  if (!cardHeader) return;

  // Create table actions container
  const actionsContainer = document.createElement('div');
  actionsContainer.className = 'datatable__table-actions';

  // Render all actions as buttons
  definition.TableActions.forEach((action, index) => {
    const buttonWrapper = document.createElement('span');
    buttonWrapper.className = 'datatable__action-btn-wrapper';
    buttonWrapper.title = action.Name;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = action.IsPrimary
      ? 'btn btn-sm btn-primary datatable__action-btn'
      : 'btn btn-sm btn-secondary datatable__action-btn';
    button.title = action.Name;
    button.setAttribute('aria-label', action.Name);

    button.dataset.actionIndex = String(index);
    button.dataset.requiresSelection = String(
      action.MassActionSetting.IsMassAction && !action.MassActionSetting.AllowWithoutSelection
    );

    // Add modal action classes and attributes (ActionType 0 = Modal)
    if (action.ActionType === 0) {
      button.classList.add('btn-modal');
      button.dataset.bizAction = action.Url;
    }

    // Add confirmation classes and attributes (ActionType 1 = Command with Confirmation)
    if (action.ActionType === 1 && action.Confirmation.RequiresConfirmation) {
      button.classList.add('btn-confirm');
      button.dataset.bizAction = action.Url;
      button.dataset.bizMessage =
        action.Confirmation.ConfirmationMessage || `Execute ${action.Name}?`;
    }

    // Icon
    const icon = document.createElement('span');
    icon.className = 'iconify';
    icon.dataset.icon = action.IconName;
    icon.dataset.height = '16';
    icon.dataset.width = '16';
    button.appendChild(icon);

    // Name for primary actions
    if (action.IsPrimary) {
      const text = document.createTextNode(` ${action.Name}`);
      button.appendChild(text);
    }

    buttonWrapper.appendChild(button);
    actionsContainer.appendChild(buttonWrapper);
  });

  cardHeader.appendChild(actionsContainer);

  // Update UI based on selection
  function updateActionUI(): void {
    const selectedIds = getSelectedIds();
    const selectedCount = selectedIds.length;

    actionsContainer
      .querySelectorAll<HTMLButtonElement>('.datatable__action-btn')
      .forEach((btn) => {
        const requiresSelection = btn.dataset.requiresSelection === 'true';
        btn.disabled = requiresSelection && selectedCount === 0;
        // Disabled buttons often suppress native title tooltips, so move hover to wrapper.
        btn.style.pointerEvents = btn.disabled ? 'none' : '';

        // Update data-biz-action with selected IDs for mass actions
        const index = parseInt(btn.dataset.actionIndex!);
        const action = definition.TableActions[index];
        const wrapper = btn.parentElement as HTMLElement | null;
        if (wrapper) {
          wrapper.title = action.Name;
        }
        btn.title = action.Name;

        if (action.MassActionSetting.IsMassAction && selectedCount > 0) {
          const url = new URL(action.Url, window.location.origin);
          selectedIds.forEach((id) => url.searchParams.append('ids', id));
          btn.dataset.bizAction = url.toString();
        } else {
          // Reset to original URL when no selection
          btn.dataset.bizAction = action.Url;
        }
      });
  }

  // Bind header checkbox (select all on page)
  const headerCheckbox = container.querySelector('thead .checks-col input[type="checkbox"]');
  headerCheckbox?.addEventListener('change', (e) => {
    const checked = (e.target as HTMLInputElement).checked;
    container.querySelectorAll('tbody .row-checkbox').forEach((cb) => {
      (cb as HTMLInputElement).checked = checked;
    });
    updateActionUI();
  });

  // Bind row checkboxes
  container.addEventListener('change', (e) => {
    if ((e.target as HTMLElement).classList.contains('row-checkbox')) {
      updateActionUI();
    }
  });

  // Bind action click handlers for non-modal, non-confirmation actions
  actionsContainer.querySelectorAll<HTMLButtonElement>('.datatable__action-btn').forEach((btn) => {
    const index = parseInt(btn.dataset.actionIndex!);
    const action = definition.TableActions[index];

    btn.addEventListener('click', async (e) => {
      const selectedIds = getSelectedIds();

      // Check if action requires selection
      if (
        action.MassActionSetting.IsMassAction &&
        !action.MassActionSetting.AllowWithoutSelection &&
        selectedIds.length === 0
      ) {
        e.preventDefault();
        e.stopImmediatePropagation();
        // eslint-disable-next-line no-console
        console.error('No rows selected for mass action:', action.Name);
        return;
      }

      // If not modal and not confirmation command, execute directly
      const isModal = action.ActionType === 0;
      const isConfirmation = action.ActionType === 1 && action.Confirmation.RequiresConfirmation;

      if (!isModal && !isConfirmation) {
        e.preventDefault();
        await executeAction(action, selectedIds);
      }

      // For modal/confirmation actions, data-biz-action is already updated by updateActionUI()
      // Let modal-fetch.ts or modal-confirm.ts handlers handle it
    });
  });

  // Initialize modal and confirmation handlers AFTER click handlers are set up
  // This ensures table-actions click handlers fire first and update data-biz-action with IDs
  initFetchModal(actionsContainer);
  initConfirmButtons(actionsContainer.querySelectorAll<HTMLElement>('.btn-confirm'));

  // Initial UI update
  updateActionUI();
}
